import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col } from 'reactstrap';
import { Link, graphql, navigate } from 'gatsby';
import Image from 'gatsby-image';
import { css, StyleSheet } from 'aphrodite';
import { gray2, fontStyles } from '@lumoslabs/react_lux';
import Eventd from '@lumoslabs/lumos_events_js';

const styles = StyleSheet.create({
  card: {
    border: `solid ${gray2} 1px`,
    borderRadius: 13,
    height: 254,
    width: 264,
    margin: 12,
    cursor: 'pointer'
  },
  cardAnimation: {
    transition: 'all 0.15s ease-in',
    ':hover': {
      transform: 'translate(0,4px)',
      boxShadow: '0 0 8px 4px rgba(0, 0, 0, 0.2)'
    }
  },
  faded: {
    opacity: 0.7
  },
  image: {
    height: 130,
    borderTopLeftRadius: 13,
    borderTopRightRadius: 13
  },
  textContainer: {
    padding: 16
  },
  textTitle: {
    fontWeight: 700
  }
});

function handleClick(gamePageUrl, eventName) {
  Eventd.event('link_click', { id: eventName, location: 'body' });
  navigate(gamePageUrl);
}

function handleKeyUp(e, gamePageUrl, eventName) {
  const enterKey = 13;
  if (e.keyCode === enterKey) {
    handleClick(gamePageUrl, eventName);
  }
}

function GameCard({ game, locale, visibleCards }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const isVisible = visibleCards.find(cardEl => cardEl.id === game.id);
    setVisible(isVisible);
  });

  const {
    name,
    urlSlug,
    englishOnly,
    cardImage: { fluid },
    shortDescription,
    longDescription: { longDescription }
  } = game;
  const eventName = `go_to_game_page_${urlSlug.replace(/-/g, '_')}`;
  const gamePageUrl = `/${locale}/brain-games/${urlSlug}/`;
  const cardDescription = shortDescription || longDescription;

  if (englishOnly && locale !== 'en') return null;

  return (
    <Col xs="auto" id={game.id}>
      <div
        // Adding click/keyup handlers here for SEO purposes.
        // Per SEO consultant, it is better to have the game title as an <a> for SEO (below).
        // The click handlers here mimic the UX behavior of the entire card being linked.
        className={css(styles.card, styles.cardAnimation, !visible && styles.faded)}
        onClick={() => handleClick(gamePageUrl, eventName)}
        onKeyUp={e => handleKeyUp(e, gamePageUrl, eventName)}
        role="link"
        tabIndex={0}
      >
        <Image fluid={fluid} className={css(styles.image)} />
        <div className={css(styles.textContainer)}>
          <Link to={gamePageUrl} tabIndex={-1}>
            <h3
              /* using an h3 tag for SEO purposes, but with p styles */
              className={css(fontStyles.p, styles.textTitle)}
            >
              {name}
            </h3>
          </Link>
          <p className={css(fontStyles.smallCaption)}>
            {cardDescription.length < 80 ? cardDescription : `${cardDescription.substring(0, 80)}...`}
          </p>
        </div>
      </div>
    </Col>
  );
}

GameCard.propTypes = {
  locale: PropTypes.string,
  game: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string
  }).isRequired,
  visibleCards: PropTypes.arrayOf(PropTypes.object).isRequired
};

GameCard.defaultProps = {
  locale: 'en'
};

export const gameCardFragment = graphql`
  fragment gameCardFragment on ContentfulGame {
    id
    name
    shortDescription
    longDescription {
      longDescription
    }
    urlSlug
    englishOnly
    cardImage {
      fluid(maxWidth: 454, quality: 100) {
        ...GatsbyContentfulFluid_withWebp_noBase64
      }
    }
  }
`;

export default GameCard;
